import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { AuthData } from "~/ssr/auth.server";
import { addAuthBreadcrumb, captureAuthEvent } from "~/utils/authTelemetry";
import { supabase } from "~/utils/supabase.client";
import { trpc } from "~/utils/trpc";

interface AuthContextValue {
  authData: AuthData | null;
}

const AuthContext = createContext<AuthContextValue>({ authData: null });

function buildAuthData(session: { user: { id: string; email?: string | null; user_metadata?: Record<string, unknown> }; access_token: string }): AuthData {
  return {
    userId: session.user.id,
    email: session.user.email ?? "",
    name:
      (session.user.user_metadata?.full_name as string) ||
      (session.user.user_metadata?.name as string) ||
      session.user.email?.split("@")[0] ||
      "Unknown",
    avatarUrl: session.user.user_metadata?.avatar_url as string | undefined,
    accessToken: session.access_token,
    // SSR is the sole source of truth for tosAgreed (auth.server.ts gates
    // every protected route via requireAuthData). Client never reads this
    // field for auth decisions — keep it that way. Defaulting to true here
    // just avoids a transient `false` on client-side session refresh.
    tosAgreed: true,
  };
}

export function AuthProvider({
  children,
  initialAuthData,
}: {
  children: React.ReactNode;
  initialAuthData: AuthData | null;
}) {
  const [authData, setAuthData] = useState<AuthData | null>(initialAuthData);
  const upsertProfile = trpc.user.upsert.useMutation();
  const upsertProfileRef = useRef(upsertProfile);
  upsertProfileRef.current = upsertProfile;

  // When server-side auth fails (e.g. cold start after redeploy), attempt
  // client-side session recovery so the user isn't unnecessarily logged out.
  useEffect(() => {
    if (initialAuthData) return;

    const recover = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (session) {
        setAuthData(buildAuthData(session));
        addAuthBreadcrumb("session.recovered");
      } else {
        captureAuthEvent("auth.recover_failed", "warning", {
          reason: error?.message ?? "no_session",
          hadInitialAuth: false,
        });
      }
    };
    recover();
  }, [initialAuthData]);

  // Sync profile on mount (if authenticated)
  useEffect(() => {
    if (!initialAuthData) return;

    const syncProfile = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      upsertProfileRef.current.mutate({
        name: session.user.user_metadata?.["full_name"] || session.user.email?.split("@")[0] || "User",
        email: session.user.email!,
        avatarUrl: session.user.user_metadata?.["avatar_url"],
      });
    };

    syncProfile();
  }, [initialAuthData]);

  // Listen for auth state changes (token refresh, sign out)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        captureAuthEvent("auth.signed_out", "warning", {
          hadAuthData: !!authData,
        });
        setAuthData(null);
        window.location.href = "/login";
      }
      if (event === "TOKEN_REFRESHED" && session) {
        addAuthBreadcrumb("token.refreshed");
        setAuthData(buildAuthData(session));
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Re-validate session when the browser tab regains focus
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      const { data, error } = await supabase.auth.getUser();
      if (error || !data.user) {
        captureAuthEvent("auth.visibility_user_missing", "warning", {
          reason: error?.message ?? "no_user",
        });
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  return (
    <AuthContext.Provider value={{ authData }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
